import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';
import {
  X,
  Clock,
  Package,
  CheckCircle2,
  Phone,
  Mail,
  HardHat,
  User,
  MapPin,
  Truck
} from 'lucide-react';

export const MaterialRequestDetailsModal = ({ isOpen, onClose, request }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !request) return null;

  const assigneeUser = request.assignedTo && typeof request.assignedTo === 'object' ? request.assignedTo : null;
  const assignee =
    assigneeUser &&
    (assigneeUser.name ||
      `${assigneeUser.firstName || ''} ${assigneeUser.lastName || ''}`.trim() ||
      assigneeUser.email);
  const assigneePhone = assigneeUser?.phone || null;
  const assigneeEmail = assigneeUser?.email || null;

  const STATUS_CONFIG = {
    PENDING: { bg: 'bg-amber-50', text: 'text-amber-600', icon: Clock, label: 'Pending' },
    PARTIAL: { bg: 'bg-blue-50', text: 'text-blue-600', icon: Package, label: 'Partial' },
    COMPLETED: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: CheckCircle2, label: 'Completed' },
    DELIVERED: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: CheckCircle2, label: 'Delivered' },
    CANCELLED: { bg: 'bg-rose-50', text: 'text-rose-600', icon: X, label: 'Cancelled' }
  };

  const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = status.icon;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300 font-poppins">
      <div className="bg-white rounded-xl w-full max-w-[720px] max-h-[95vh] min-h-0 border border-slate-200 flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
          <div className="flex flex-col">
            <h2 className="font-semibold text-[15px] text-brand-dark">Material Request Details</h2>
            <div className="flex items-center gap-2 mt-0.5">
               <span className="text-[12px] font-bold text-[#f97316] uppercase tracking-wider">
                 Project ID: {request.project?.projectId || 'N/A'}
               </span>
               <div className="w-1 h-1 rounded-full bg-slate-200" />
               <span className="text-[10px] font-semibold text-slate-400">
                 {request.project?.projectName || 'N/A'}
               </span>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all cursor-pointer"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 no-scrollbar space-y-4">
          
          {/* Status Bar */}
          <div className="flex items-center justify-between px-1">
             <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status.bg} ${status.text}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                <span className="text-[11px] font-semibold text-slate-700 tracking-wider">{status.label}</span>
             </div>
             <div className="flex flex-col items-end">
                <span className="text-[11px] font-semibold text-slate-400 tracking-tight">Request ID: {request._id.slice(-8).toUpperCase()}</span>
                <span className="text-[11px] font-semibold text-slate-700 mt-0.5 tabular-nums">
                   {request.createdAt ? new Date(request.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                </span>
             </div>
          </div>

          <div className="p-4 border border-blue-100/50 rounded-xl bg-blue-50/20 space-y-3">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-blue-600 tracking-wider">
              <HardHat className="w-3.5 h-3.5" />
              Requesting Entity
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-100 bg-white p-3 space-y-1 shadow-sm">
                <span className="text-[11px] font-semibold text-slate-400 tracking-wider">Department</span>
                <p className="text-[13px] font-semibold text-slate-800">
                  {request.installationTrade ? formatInstallationTrade(request.installationTrade) : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-white p-3 space-y-2 shadow-sm">
                <span className="text-[11px] font-semibold text-slate-400 tracking-wider">Requested to</span>
                <p className="text-[13px] font-bold text-slate-800">
                  {assignee || 'Department Auto-Request'}
                </p>
                {(assigneePhone || assigneeEmail) && (
                  <div className="flex flex-col gap-1.5 pt-1.5 border-t border-slate-50">
                    {assigneePhone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3 text-slate-400" />
                        <span className="text-[11px] font-semibold text-slate-500">{assigneePhone}</span>
                      </div>
                    )}
                    {assigneeEmail && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-slate-400" />
                        <span className="text-[11px] font-semibold text-slate-500 truncate">{assigneeEmail}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
             {/* Customer Info */}
             <div className="p-4 border border-slate-100 rounded-xl bg-slate-50/40 space-y-3">
                <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-400 tracking-wider">
                   <User className="w-3 h-3" />
                   Customer Info
                </div>
                <div className="space-y-2.5">
                   <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 shadow-sm">
                         <User className="w-3 h-3" />
                      </div>
                      <span className="text-[13px] font-bold text-slate-700">{request.project?.customerName || 'N/A'}</span>
                   </div>
                   <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 shrink-0 shadow-sm">
                         <MapPin className="w-3 h-3" />
                      </div>
                      <span className="text-[11px] font-medium text-slate-500 leading-snug">
                         {request.project?.location || 'No location set'}
                      </span>
                   </div>
                </div>
             </div>

             {/* Project Manager */}
             <div className="p-4 border border-slate-100 rounded-xl bg-slate-50/40 space-y-3">
                <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-400 tracking-wider">
                   <Package className="w-3 h-3" />
                   Project Authority
                </div>
                <div className="space-y-2.5">
                   <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 shadow-sm">
                         <HardHat className="w-3 h-3" />
                      </div>
                      <span className="text-[13px] text-slate-700">
                         {request.project?.projectManager?.name || 'Awaiting PM Assignment'}
                      </span>
                   </div>
                   <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 shadow-sm">
                         <Phone className="w-3 h-3" />
                      </div>
                      <span className="text-[11px] font-medium text-slate-500 tabular-nums">
                         {request.project?.projectManager?.phone || 'N/A'}
                      </span>
                   </div>
                </div>
             </div>
          </div>

          {/* Materials Table */}
          <div className="space-y-2.5">
             <div className="flex items-center justify-between px-1">
                <span className="text-[12px] text-slate-800 font-medium tracking-wider">Requested Items</span>
                <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded uppercase">{request.items.length} units total</span>
             </div>
             <div className="border border-slate-100  overflow-hidden shadow-sm">
                <table className="w-full text-left">
                   <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider">Material Detail</th>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider">Vendor</th>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider text-center">Req Qty</th>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider text-center">Delivered</th>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider text-right">Available</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-50">
                      {request.items.map((item, idx) => (
                        <tr key={idx} className="bg-white hover:bg-slate-50/50 transition-colors">
                           <td className="px-5 py-3.5">
                              <div className="flex flex-col">
                                 <span className="text-[12px] font-medium text-slate-800 tracking-tight">{item.itemName}</span>
                                 <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-medium text-slate-400 tracking-tight uppercase">
                                       {item.inventoryId?.item?.category?.categoryName || item.inventoryId?.item?.category?.title || 'General Stock'}
                                    </span>
                                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1 rounded">
                                       ₹{item.inventoryId?.unitPrice || item.inventoryId?.purchaseRate || item.inventoryId?.mrp || item.inventoryId?.blumPrice || item.inventoryId?.vervePrice || item.inventoryId?.price || item.inventoryId?.item?.mrp || item.inventoryId?.item?.blumPrice || item.inventoryId?.item?.vervePrice || item.inventoryId?.item?.price || item.unitPrice || item.vervePrice || item.purchaseAmount || 0}
                                    </span>
                                 </div>
                              </div>
                           </td>
                           <td className="px-5 py-3.5">
                              <span className="text-[11px] font-medium text-slate-600 truncate max-w-[100px] block" title={item.inventoryId?.vendor?.vendorName || 'In-House'}>
                                 {item.inventoryId?.vendor?.vendorName || 'In-House'}
                              </span>
                           </td>
                           <td className="px-5 py-3.5 text-center">
                              <span className="text-[12px] font-medium text-slate-700 tabular-nums tracking-tight">{item.quantity}</span>
                           </td>
                           <td className="px-5 py-3.5 text-center">
                              <span className={`text-[11px] font-medium tabular-nums tracking-tight ${item.deliveredQuantity > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                 {item.deliveredQuantity || 0}
                                 <span className="text-slate-300 mx-1">/</span>
                                 {item.quantity}
                              </span>
                           </td>
                           <td className="px-5 py-3.5 text-right">
                              <span className="text-[11px] font-medium text-slate-500 tabular-nums tracking-tight">{item.inventoryId?.stock || '0'}</span>
                           </td>
                        </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </div>

          {/* Dispatch Details */}
          {request.dispatchDetails?.dispatchedAt && (
            <div className="p-4 bg-blue-50/30 border border-blue-100/50 rounded-xl space-y-3">
               <div className="flex items-center gap-2 text-[10px] font-black text-blue-600 uppercase tracking-widest">
                  <Truck className="w-3.5 h-3.5" />
                  Dispatch Information
               </div>
               <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-0.5">
                     <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Driver / Logistics</span>
                     <span className="text-[12px] font-bold text-slate-700">{request.dispatchDetails.driverName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                     <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Vehicle Reg No</span>
                     <span className="text-[12px] font-bold text-slate-700">{request.dispatchDetails.vehicleNumber || 'N/A'}</span>
                  </div>
               </div>
               {request.dispatchDetails.notes && (
                  <div className="pt-2 border-t border-blue-100/30">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Dispatch Notes</span>
                    <p className="text-[11px] text-slate-600 font-medium italic mt-1 leading-relaxed">"{request.dispatchDetails.notes}"</p>
                  </div>
               )}
            </div>
          )}

          {/* Delivery / Receive Details */}
          {request.receiveDetails?.receivedAt && (
            <div className="p-4 bg-emerald-50/30 border border-emerald-100/50 rounded-xl space-y-3">
               <div className="flex items-center gap-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Site Receipt Confirmation
               </div>
               <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Acknowledgement Notes</span>
                  <span className="text-[12px] font-bold text-slate-700 leading-snug">{request.receiveDetails.statusNotes || 'Received without notes'}</span>
               </div>
               
               {request.receiveDetails.photos?.length > 0 && (
                 <div className="space-y-2 pt-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Site Verification Photos</span>
                    <div className="flex flex-wrap gap-2">
                       {request.receiveDetails.photos.map((url, idx) => {
                         const fileUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '') + '/' + url;
                         
                         const handleDownload = async (e) => {
                           e.preventDefault();
                           try {
                             const response = await fetch(fileUrl);
                             const blob = await response.blob();
                             const blobUrl = window.URL.createObjectURL(blob);
                             const link = document.createElement('a');
                             link.href = blobUrl;
                             link.download = url.split('/').pop() || `site-photo-${idx}.jpg`;
                             document.body.appendChild(link);
                             link.click();
                             document.body.removeChild(link);
                             window.URL.revokeObjectURL(blobUrl);
                           } catch (error) {
                             console.error('Download failed:', error);
                             window.open(fileUrl, '_blank');
                           }
                         };
 
                         return (
                           <button 
                             key={idx} 
                             onClick={handleDownload}
                             className="w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-white hover:ring-2 hover:ring-emerald-500 transition-all cursor-pointer shadow-sm group"
                           >
                              <img 
                                src={fileUrl} 
                                alt="Site receipt photo" 
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                              />
                           </button>
                         );
                       })}
                    </div>
                 </div>
               )}
            </div>
          )}

          {request.notes && (
            <div className="p-4 border border-slate-100 border-dashed rounded-xl bg-slate-50/20">
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Requester Remarks</span>
               <p className="text-[12px] text-slate-600 font-medium leading-relaxed italic">
                  "{request.notes}"
               </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 p-5 border-t border-slate-100 flex items-center bg-white">
          <button 
            onClick={onClose}
            className="w-full py-3 bg-[#64748b] hover:bg-[#475569] text-white rounded-lg text-[13px] font-bold uppercase tracking-widest transition-all shadow-md shadow-blue-500/10 cursor-pointer"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
